import { useState, useEffect } from 'react';
import useSWR, { type SWRConfiguration } from 'swr';

import { debounce } from '@mui/material';

type UseDebouncedSWRParams = {
  fetchUrl: string | null;
  fetchFn: (url: string) => Promise<any>;
  swrConfig?: SWRConfiguration;
  debounceDelay?: number;
};

export const useSWRWithDebounce = <T, E>({
  fetchUrl,
  fetchFn,
  debounceDelay = 500,
  swrConfig,
}: UseDebouncedSWRParams) => {
  const [uniqueKey, setUniqueKey] = useState<string | null>(null);

  useEffect(() => {
    if (!fetchUrl) {
      setUniqueKey(null);
      return;
    }

    if (debounceDelay === 0) {
      setUniqueKey(fetchUrl);
      return;
    }

    const keyHandler = debounce(() => setUniqueKey(fetchUrl), debounceDelay);
    keyHandler();

    return () => keyHandler.clear();
  }, [fetchUrl, debounceDelay]);

  const swr = useSWR<T, E>(uniqueKey, fetchFn, swrConfig);

  // `uniqueKey` trails `fetchUrl` — that lag is what debounces a search term. It also
  // means the data being returned may belong to an *earlier* request than the caller is
  // now asking for. Callers that act on the data (rather than just display it) need to
  // know that, so surface the key the data actually belongs to.
  return { ...swr, activeKey: uniqueKey };
};
